/*  MUI  */
import { Box, CircularProgress } from '@mui/material';
import { OktaAuth } from '@okta/okta-auth-js';
import { Security } from '@okta/okta-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

/*  Shared  */
import { PermissionsErrorPage } from 'application/Shared/Components/PermissionsErrorPage';
import { Browser } from 'application/Shared/Handlers';
import {
    Helpers,
    Response,
    SessionHelper,
    URL
} from 'application/Shared/Helpers';
import ScrollToTop from 'application/Shared/Helpers/ScrollToTop';
import { Requests } from 'application/Shared/Requests';
import { Env, debug } from 'application/Shared/Utils';

/*  NPM  */
import axios from 'axios';
import {
    applyAuthTokenInterceptor,
    clearAuthTokens,
    getAccessToken
} from 'axios-jwt';
import 'bootstrap-print-css/css/bootstrap-print.min.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'font-awesome/css/font-awesome.min.css';

/*  Objects  */
import Session from 'objects/Session';

/*  React  */
import React, { Component } from 'react';
import { Navigate, Route, BrowserRouter as Router } from 'react-router';

/*  NPM  */
import { ToastContainer } from 'react-toastify';

import { configureSentryScope } from '../../sentry';
import { BroadcastChannelContextProvider } from './BroadcastChannelContext';

/*  Components  */
import { TanstackDevtools } from './Components/TanstackDevtools';
import { AppRoutes } from './Routes';

/*  Constants  */
const { isset } = Helpers;
const { hasData } = Response;
const queryClient = new QueryClient();

const handleCSS = () => {
    const height = '100%';
    const width = '100%';
    const elements = document.querySelectorAll('.App');
    for (let i = 0; i < elements.length; i += 1) {
        elements[i].style.width = width;
        elements[i].style.height = height;
    }
};

class App extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: true,
            redirectTo: null,
            rerender: false
        };

        /* Okta */
        this.oktaAuth = null;

        /* requests */
        this.requests = new Requests();
    }

    componentDidMount() {
        this.load();
    }

    componentWillUnmount() {
        this.unmounted = true;
        this.unload();
    }

    load = async () => {
        // const { match } = this.props;
        /* Okta */
        const issuer = Env.get('REACT_APP_OKTA_ISSUER');
        const authorizeUrl = Env.get('REACT_APP_OKTA_AUTHORIZE_URL');
        const oktaClientId = Env.get('REACT_APP_OKTA_CLIENT_ID');

        if (!issuer || !authorizeUrl || !oktaClientId) {
            debug.w('No Okta Auth', issuer, authorizeUrl, oktaClientId);
            return;
        }

        this.oktaAuth = new OktaAuth({
            authorizeUrl,
            clientId: oktaClientId,
            issuer,
            pkce: false,
            redirectUri: `${window.location.origin}/okta/callback`,
            responseType: 'code'
        });

        Env.describe();

        // Default axios - do not include credentials unnecessarily
        axios.defaults.withCredentials = false;

        // Apply AxiosJWT to default axios - export to avoid linter issue, even though not used directly
        const requestRefresh = async (token) => {
            try {
                const newToken = await Session.refreshJWT();
                if (newToken) {
                    await Session.storeJWT(newToken, () => {});
                }
                return newToken;
            } catch (error) {
                debug.e('Error refreshing token', error);
                await clearAuthTokens();

                // If we had a token before, we need to end the session and redirect client to logout
                if (token) {
                    await Session.end(() => {
                        this.props.setSessionInfo(null);
                        this.setState({ redirectTo: '/logout' });
                    });
                }
                return '';
            }
        };

        // Add the auth-token intercetor which handles refreshing token
        applyAuthTokenInterceptor(axios, {
            getStorage: () => Session.STORAGE,
            headerPrefix: '',
            requestRefresh,
            tokenExpireFudge: '60s' // Pre-expire accessToken if with-in 60s
        });

        // Add route interceptors on default axios
        Session.axiosRouteInterceptors(
            this.validateSessionWithOktaCallback,
            queryClient
        );

        // Allow accessToken (JWT) to be provided via URLSearchParams (temporary w/o refreshToken)
        // This is used by scheduled reports that use Puppeteer to navigate to avoid issue getting Cookie set
        const { pathname, search } = window.location;
        const searchParams = new URLSearchParams(search);
        const token = searchParams.get('token');
        if (token) {
            await Session.storeJWT(token, () => {});
        }

        // If we do not have a JWT (typical on app reload) we should attempt to refresh with cookie (if possible)
        if (!(await getAccessToken())) {
            const forceLogout =
                pathname === '/logout' &&
                searchParams.get('forceLogout') === 'true';

            // Only do a refresh if we are not in the process of going to logout!
            if (!forceLogout) {
                await requestRefresh(null);
            }
        }

        const userSession = await this.getSessionInfo();
        const sessionHelper = new SessionHelper(userSession);
        const tenant = sessionHelper.getTenant();

        Browser.handleTabTitle({ tenant });

        if (sessionHelper.exists()) {
            await this.handleSession();

            // Now when we have the session we set Sentry scope.
            configureSentryScope(userSession, process.env.REACT_APP_ENV);
        }

        if (!this.unmounted) {
            this.setState({ loading: false }, () => {
                handleCSS();
            });
        }
    };

    unload = () => {
        this.requests.cancel();
    };

    /**
     * Callback triggered after okta session validation is performed in Session.js
     * If there is no valid session, clean up session info from props and navigate to /logout.
     * @param {*} response is null if no session exists, otherwise session object.
     */
    validateSessionWithOktaCallback = (response) => {
        if (!response) {
            debug.w('Okta session invalid or expired');
            Session.end(() => {
                this.props.setSessionInfo(null);
                this.setState({ redirectTo: '/logout' });
            });
        }
    };

    getSessionInfo = async () => {
        const { setSessionInfo } = this.props;

        if (!(await getAccessToken())) {
            debug.w('NO JWT');
            return false;
        }

        let userSession;
        try {
            userSession = await Session.getSessionInfo();
            debug.i('Get Session Info Response', userSession);
            // enable session validation (for okta), since session exists.
            Session.enableOktaSessionValidation(true);
        } catch (e) {
            debug.e('Error getting Session', e);
            return false;
        }

        setSessionInfo(userSession);
        return userSession;
    };

    // TODO: WTF is this for? Just to validate that an API call can be made successfully?
    // Can we remove this? If yes, then we can remove the URL.isPartner and URL.isReport helpers in the URL helpers file.
    handleSession = async () => {
        try {
            const response = await axios.get('/tenant/self', {
                cancelToken: this.requests.token()
            });
            if (!hasData(response)) {
                return false;
            }

            debug.i('Handle Session Response', response);
            return true;
        } catch (e) {
            debug.w('Handle Session Error', e);
            const { location } = window;
            // TODO: What is this for? This seems like it can be removed.
            if (URL.isPartner(location) || URL.isReport(location)) {
                debug.i('Redirecting to /logout');
                this.props.setSessionInfo(null);
                this.setState({ redirectTo: '/logout' });
            }
            return false;
        }
    };

    render() {
        const { session } = this.props;
        const { loading, redirectTo, rerender } = this.state;

        if (redirectTo === '/logout' && !session) {
            window.location.href = '/logout?forceLogout=true';
            return null;
        }

        if (loading) {
            return (
                <Box
                    className="App"
                    id="App"
                    sx={{
                        backgroundColor: 'background.primary',
                        height: '100%'
                    }}
                >
                    <Box
                        className="no-print"
                        style={{
                            display: 'block',
                            left: '50%',
                            margin: 'auto',
                            position: 'absolute',
                            textAlign: 'center',
                            top: ' 25%'
                        }}
                    >
                        <CircularProgress />
                    </Box>
                </Box>
            );
        }

        if (
            isset(session, 'user', 'permission', 'name') &&
            session.user.permission.name === 'No UI Access' &&
            isset(session, 'user', 'permission', 'defaultPermission') &&
            session.user.permission.defaultPermission
        ) {
            return (
                <Router
                    future={{
                        v7_relativeSplatPath: true,
                        v7_startTransition: true
                    }}
                >
                    <Route path="*" component={PermissionsErrorPage} />
                </Router>
            );
        }

        return (
            <div className="App" id="App">
                <QueryClientProvider client={queryClient}>
                    <BroadcastChannelContextProvider>
                        <Router
                            future={{
                                v7_relativeSplatPath: true,
                                v7_startTransition: true
                            }}
                        >
                            <ScrollToTop />
                            {redirectTo && <Navigate to={redirectTo} replace />}
                            <Box
                                width="100%"
                                height="100%"
                                data-trackid="appContainer"
                            >
                                {this.oktaAuth ? (
                                    <Security
                                        oktaAuth={this.oktaAuth}
                                        restoreOriginalUri={() => {}}
                                    >
                                        <AppRoutes
                                            session={session}
                                            rerender={rerender}
                                        />
                                    </Security>
                                ) : (
                                    <AppRoutes
                                        session={session}
                                        rerender={rerender}
                                    />
                                )}
                            </Box>
                            <ToastContainer
                                closeButton={false}
                                closeOnClick
                                style={{ zIndex: 11000 }} // Because flyout has 10k z-index
                            />
                        </Router>
                        <TanstackDevtools />
                    </BroadcastChannelContextProvider>
                </QueryClientProvider>
            </div>
        );
    }
}

export default App;
